import { useEffect, useMemo } from 'react';

import { extractLastNameComponent } from '@spotify/confidence-core-react';
import { getTypeOrNull } from '@spotify/confidence-graphql';
import {
  deriveFavoritesCollectionName,
  useCollectionEntries,
} from '@spotify/plugin-confidence-collections-react';
import { useCurrentIdentity } from '@spotify/plugin-confidence-iam-react';

export type FavouriteProject = {
  id: string;
  name: string;
  resourceName: string;
};

export const useFavouriteProjects = (): {
  projects: FavouriteProject[];
  isLoading: boolean;
} => {
  const { currentIdentity, isLoading: identityLoading } = useCurrentIdentity();
  const collectionName = currentIdentity?.name
    ? deriveFavoritesCollectionName(currentIdentity.name)
    : undefined;

  const {
    collectionEntries,
    isLoading: entriesLoading,
    hasNextPage,
    nextPage,
  } = useCollectionEntries({
    parent: collectionName!,
    skip: !collectionName,
  });

  // Fetch all pages eagerly to build the full set
  useEffect(() => {
    if (hasNextPage && !entriesLoading) {
      nextPage();
    }
  }, [hasNextPage, entriesLoading, nextPage]);

  const projects = useMemo(
    () =>
      (collectionEntries ?? []).flatMap(entry => {
        const resource = getTypeOrNull(entry.resource);
        if (!resource || resource.__typename !== 'CollaborationV1Project')
          return [];
        return [
          {
            id: extractLastNameComponent(resource.name) ?? resource.name,
            name: resource.displayName,
            resourceName: resource.name,
          },
        ];
      }),
    [collectionEntries],
  );

  return {
    projects,
    isLoading: identityLoading || entriesLoading || hasNextPage,
  };
};
